import { forwardRef, type TextareaHTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  error?: boolean;
  size?: "sm" | "md" | "lg";
};

type TextareaSize = "sm" | "md" | "lg";

interface TextareaOwnerState {
  size: TextareaSize;
  error: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline, except the
 * focus ring/outline was removed so focused text areas render a single border. */

const textareaBase: CSSObject = {
  width: "100%",
  resize: "vertical",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "none",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:disabled::placeholder": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&:focus": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  "&:focus-visible": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    WebkitTextFillColor: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
};

const textareaSizeStyles: Record<TextareaSize, CSSObject> = {
  sm: {
    minHeight: "4rem",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem"
  },
  md: {
    minHeight: "6rem",
    paddingLeft: ".875rem",
    paddingRight: ".875rem",
    paddingTop: ".75rem",
    paddingBottom: ".75rem",
    fontSize: "1rem",
    lineHeight: "1.5rem"
  },
  lg: {
    minHeight: "8rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: ".875rem",
    paddingBottom: ".875rem",
    fontSize: "1rem",
    lineHeight: "1.5rem"
  }
};

const textareaError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
};

const textareaDefaultBorder: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
};

const TextareaRoot = styled("textarea", {
  label: "AgenticTextarea-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: TextareaOwnerState }>(({ ownerState }) => [
  textareaBase,
  textareaSizeStyles[ownerState.size],
  ownerState.error ? textareaError : textareaDefaultBorder
]);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, size = "md", error, "aria-invalid": ariaInvalid, rows = 4, ...props }, ref) => (
    <TextareaRoot
      ref={ref}
      rows={rows}
      aria-invalid={ariaInvalid ?? error}
      ownerState={{ size, error: Boolean(error) }}
      className={className}
      {...props}
    />
  )
);

Textarea.displayName = "Textarea";
